import classNames from 'classnames';
import type { ReactNode } from 'react';

import { Box, type BoxProps, Inline, Text } from '../index';
import {
  dialogBorder,
  dialogBoxShadow,
  dialogFooter,
  dialogHeaderStyle,
  dialogStyle,
  roundBottomStyle,
  roundTopStyle,
  v2509DialogContainer,
  v2509DialogContent,
  v2509DialogContentScroll,
  v2509DialogFooter,
  v2509DialogHeaderStyle,
  v2509RoundBottomStyle,
  v2509RoundTopStyle,
  v2512DialogContent,
  v2512DialogHeaderStyle,
} from './Dialog.css';
import { DialogCloseButton } from './DialogCloseButton';

interface DialogProps {
  drawBorder?: boolean;
  drawBoxShadow?: boolean | null | undefined;
  roundBottom?: boolean;
  roundTop?: boolean;
  scroll?: boolean;
  children: ReactNode;
  variant?: 'v2509' | 'v2512' | null | undefined;
}

export function Dialog({
  drawBorder = true,
  drawBoxShadow = null,
  roundTop = true,
  roundBottom = true,
  scroll = true,
  children,
  variant,
}: DialogProps): JSX.Element {
  if (variant === 'v2509' || variant === 'v2512') {
    return (
      <Box
        background="body"
        className={classNames(
          dialogStyle,
          drawBorder ? dialogBorder : '',
          drawBoxShadow === true ? dialogBoxShadow : '',
          roundTop ? v2509RoundTopStyle : '',
          roundBottom ? v2509RoundBottomStyle : '',
          v2509DialogContainer,
        )}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      background="body"
      className={classNames(
        dialogStyle,
        drawBorder ? dialogBorder : '',
        drawBoxShadow ? dialogBoxShadow : '',
        roundTop ? roundTopStyle : '',
        roundBottom ? roundBottomStyle : '',
      )}
    >
      {children}
    </Box>
  );
}

export const DialogHeader = ({
  title,
  leadingIcon,
  onClose,
  variant,
}: {
  title: string;
  variant?: 'v2509' | 'v2512' | 'default' | null | undefined;
  leadingIcon?: ReactNode;
  onClose?: () => void;
}): JSX.Element => {
  if (variant === 'v2512') {
    return (
      <Box
        className={v2512DialogHeaderStyle}
        display="flex"
        justifyContent="spaceBetween"
        alignItems="center"
      >
        <Inline alignY="center" gap={leadingIcon ? 'medium' : 'none'}>
          {leadingIcon && leadingIcon}
          <Text size="large" weight="demibold">
            {title}
          </Text>
        </Inline>
        {onClose && <DialogCloseButton onClose={onClose} />}
      </Box>
    );
  }

  if (variant === 'v2509') {
    return (
      <Box className={v2509DialogHeaderStyle} justifyContent="spaceBetween">
        <Inline alignY="center" gap={leadingIcon ? 'medium' : 'none'}>
          {leadingIcon && leadingIcon}
          <Text size="medium" weight="demibold">
            {title}
          </Text>
        </Inline>
        {onClose && <DialogCloseButton onClose={onClose} />}
      </Box>
    );
  }

  return (
    <Box
      className={dialogHeaderStyle}
      display="flex"
      justifyContent="spaceBetween"
      alignItems="center"
    >
      <Inline alignY="center" gap={leadingIcon ? 'medium' : 'none'}>
        {leadingIcon && leadingIcon}
        <Text size="large" weight="demibold">
          {title}
        </Text>
      </Inline>
      {onClose && <DialogCloseButton onClose={onClose} />}
    </Box>
  );
};

interface DialogContentProps extends BoxProps {
  children: ReactNode;
  padding?: BoxProps['padding'];
  variant?: 'default' | 'v2509' | 'v2512' | null | undefined;
  dialogSize?: 'medium' | 'large' | null | undefined;
}

export const DialogContent = ({
  children,
  padding = 'medium',
  variant = 'default',
  dialogSize = 'medium',
  ...restProps
}: DialogContentProps): JSX.Element => {
  if (variant === 'v2509') {
    const widths = {
      medium: '450px',
      large: '550px',
    } as const;
    const width = dialogSize && widths[dialogSize] ? widths[dialogSize] : widths.medium;
    return (
      <Box
        style={{ width }}
        className={classNames(v2509DialogContent, v2509DialogContentScroll)}
        {...restProps}
      >
        {children}
      </Box>
    );
  } else if (variant === 'v2512') {
    const widths = {
      medium: '450px',
      large: '550px',
    } as const;
    const width = dialogSize && widths[dialogSize] ? widths[dialogSize] : widths.medium;
    return (
      <Box style={{ width }} className={classNames(v2512DialogContent)} {...restProps}>
        {children}
      </Box>
    );
  } else {
    return <Box padding={padding}>{children}</Box>;
  }
};

export const DialogFooter = ({
  children,
  variant,
}: {
  children: ReactNode;
  variant?: 'v2509' | 'v2512' | null | undefined;
}): JSX.Element => {
  if (variant === 'v2509') {
    return (
      <Box background="body" className={classNames(v2509DialogFooter, v2509RoundBottomStyle)}>
        {children}
      </Box>
    );
  }
  return (
    <Box background="body" className={classNames(dialogFooter, roundBottomStyle)}>
      {children}
    </Box>
  );
};

export const StickyBottom = ({ children }: { children: ReactNode }): JSX.Element => {
  return (
    <Box style={{ zIndex: 5 }} position="sticky" bottom={0} left={0} right={0}>
      {children}
    </Box>
  );
};
